/**
 * Iter51 Etapa 9 — ClientImportHistory: import history tab on ClientDetail.
 *
 * Shows batches that touched this client's contracts, grouped by import_batch_id.
 * Same card pattern as SaleOverview/ClientOverview.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  FileText, Calendar, Database, Cloud, Loader2, AlertTriangle,
  CheckCircle2, ArrowRight, Hash,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ro-RO", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const SOURCE_META = {
  salesforce: { label: "Salesforce", icon: Cloud, color: "purple" },
  easy: { label: "Easy", icon: Database, color: "blue" },
  crm: { label: "CRM", icon: Database, color: "indigo" },
  manual: { label: "Manual", icon: FileText, color: "slate" },
};

const BatchCard = ({ batch }) => {
  const meta = SOURCE_META[batch.source] || { label: batch.source || "Necunoscut", icon: FileText, color: "slate" };
  const Icon = meta.icon;
  const failed = batch.status === "failed";
  return (
    <Card className="shadow-sm border-slate-200" data-testid={`batch-card-${batch.batch_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`h-10 w-10 rounded-md bg-${meta.color}-50 text-${meta.color}-700 flex items-center justify-center flex-shrink-0`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-slate-900">{meta.label}</p>
                <Badge className={
                  failed ? "bg-red-100 text-red-700 border-red-200" :
                  batch.status === "completed" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                  "bg-amber-100 text-amber-700 border-amber-200"
                }>
                  {batch.status}
                </Badge>
              </div>
              <p className="text-xs text-slate-500 truncate font-mono mt-0.5">{batch.filename || "(fără nume fișier)"}</p>
              <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> {fmtDate(batch.imported_at)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/imports?batch=${batch.batch_id}`}>
              <button className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid={`view-batch-${batch.batch_id}`}>
                Detalii batch <ArrowRight className="h-3 w-3" />
              </button>
            </Link>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
          <div className="text-center rounded-md bg-slate-50 px-2 py-1.5">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Total</p>
            <p className="text-sm font-semibold text-slate-900">{batch.rows_total ?? 0}</p>
          </div>
          <div className="text-center rounded-md bg-emerald-50 px-2 py-1.5">
            <p className="text-[10px] uppercase tracking-wider text-emerald-600">Create</p>
            <p className="text-sm font-semibold text-emerald-700">{batch.rows_created ?? 0}</p>
          </div>
          <div className="text-center rounded-md bg-blue-50 px-2 py-1.5">
            <p className="text-[10px] uppercase tracking-wider text-blue-600">Actualizate</p>
            <p className="text-sm font-semibold text-blue-700">{batch.rows_updated ?? 0}</p>
          </div>
          <div className="text-center rounded-md bg-slate-50 px-2 py-1.5">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Sărite</p>
            <p className="text-sm font-semibold text-slate-700">{batch.rows_skipped ?? 0}</p>
          </div>
          <div className={`text-center rounded-md ${batch.rows_errors > 0 ? "bg-red-50" : "bg-slate-50"} px-2 py-1.5`}>
            <p className={`text-[10px] uppercase tracking-wider ${batch.rows_errors > 0 ? "text-red-600" : "text-slate-500"}`}>Erori</p>
            <p className={`text-sm font-semibold ${batch.rows_errors > 0 ? "text-red-700" : "text-slate-700"}`}>{batch.rows_errors ?? 0}</p>
          </div>
        </div>

        {/* Sales affected list */}
        <div className="border-t border-slate-100 pt-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
            Vânzări atinse pentru acest client ({batch.sales.length})
          </p>
          <div className="space-y-1.5">
            {batch.sales.map((s) => (
              <Link to={`/sales/${s.sale_id}`} key={s.sale_id} className="block hover:bg-slate-50 -mx-2 px-2 py-1.5 rounded transition-colors group" data-testid={`sale-link-${s.sale_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <Hash className="h-3 w-3 text-slate-400 flex-shrink-0" />
                    <span className="text-xs font-mono text-slate-600">{s.pod || s.contract_number || "(fără POD)"}</span>
                    {s.product_name && (
                      <Badge variant="outline" className="text-[10px] font-normal">{s.product_name}</Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400 flex items-center gap-2">
                    <Badge className="text-[10px]" variant="outline">{s.commodity || "—"}</Badge>
                    <span>{fmtDate(s.first_imported_at)}</span>
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ClientImportHistory = ({ clientId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/clients/${clientId}/import-history`);
        setData(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Eroare la încărcare istoric");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [clientId]);

  if (loading && !data) {
    return (
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-12 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { batches = [], manual_sales = [], total_batches, total_sales } = data;

  if (total_sales === 0) {
    return (
      <Card className="border-slate-200 shadow-sm" data-testid="empty-import-history">
        <CardContent className="p-10 text-center">
          <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-700">Nicio vânzare/contract pentru acest client.</p>
          <p className="text-xs text-slate-500 mt-1">Când acest client va apărea într-un import (SF, Easy, CRM) sau va fi creat manual, istoricul apare aici.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="client-import-history">
      {/* Summary */}
      <Card className="border-slate-200 shadow-sm" data-testid="import-history-summary">
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Total batch-uri</p>
              <p className="text-xl font-semibold text-slate-900" data-testid="total-batches">{total_batches}</p>
            </div>
            <div className="h-8 w-px bg-slate-200" />
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Total vânzări</p>
              <p className="text-xl font-semibold text-slate-900" data-testid="total-sales">{total_sales}</p>
            </div>
            {manual_sales.length > 0 && (
              <>
                <div className="h-8 w-px bg-slate-200" />
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">Manuale</p>
                  <p className="text-xl font-semibold text-slate-900">{manual_sales.length}</p>
                </div>
              </>
            )}
          </div>
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
        </CardContent>
      </Card>

      {/* Batches */}
      {batches.map((b) => <BatchCard key={b.batch_id} batch={b} />)}

      {/* Manual sales (no batch) */}
      {manual_sales.length > 0 && (
        <Card className="border-slate-200 shadow-sm" data-testid="manual-sales-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Vânzări create manual (fără batch)</p>
            </div>
            <div className="space-y-1.5">
              {manual_sales.map((s) => (
                <Link to={`/sales/${s.sale_id}`} key={s.sale_id} className="block hover:bg-slate-50 -mx-2 px-2 py-1.5 rounded transition-colors group">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Hash className="h-3 w-3 text-slate-400" />
                      <span className="text-xs font-mono text-slate-600">{s.pod || s.contract_number || "(fără POD)"}</span>
                      {s.product_name && <Badge variant="outline" className="text-[10px]">{s.product_name}</Badge>}
                    </div>
                    <span className="text-[11px] text-slate-400">{fmtDate(s.first_imported_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientImportHistory;
